import { Divider, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { readContract } from '@wagmi/core'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import airdropScreenshot from '@/assets/webp_converted/airdropScreenshot.webp'
import { AIRDROP_CONTRACT_ADDRESS } from '@/constants/blockchain'
import ClaimableAirdrop from '@/contracts/ClaimableAirdrop'
import useCopyClipboard from '@/hooks/useCopyToClipboard'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig } from '@/plugins/auth/config'

import { callbackCopyMessage } from '../Buttons/CopyButton'
import RootButton from '../Buttons/RootButton'
import classes from '../Transfer/Send.module.css'
import ErrorSyncingAirdrop from './ErrorSyncingAirdrop'
import NeedHelp from './NeedHelp'
import SyncingAirdrop from './SyncingAirdrop'
import {
  AIRDROP_API_KEY,
  AIRDROP_GUIDE,
  AIRDROP_URL,
  AirdropRecipient,
  DISCORD_AIRDROP,
  LinkedRewards,
  Reward,
  RewardKey,
} from './utils'

interface LinkDiscordProps {
  skip: () => void
}

const LinkDiscord: React.FC<LinkDiscordProps> = ({ skip }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address } = useAuth()
  const [, staticCopy] = useCopyClipboard()

  const copyHubAddress = useCallback(() => {
    staticCopy(address || '', classes, theme, callbackCopyMessage(t))
  }, [address, classes, staticCopy, theme, t])

  const openDiscord = useCallback(() => {
    window.open(DISCORD_AIRDROP, '_blank')
  }, [])

  return (
    <Stack style={{ gap: '20px' }}>
      <Title order={isMobile ? 3 : 2}>
        {t('components.airdrop.complete', 'Link Discord to check for community rewards')}
      </Title>
      <Text style={{ fontSize: 14 }}>
        {t(
          'components.airdrop.completeDescription',
          'Next, connect your Discord to your on-chain account to see if you are eligible for any additional rewards based on your Discord or Zealy activity.'
        )}
      </Text>
      <Stack style={{ flexDirection: isMobile ? 'column' : 'row', gap: 30 }}>
        <Title order={4} style={{ maxWidth: 204 }}>
          {t('components.airdrop.copyAddress', '1. Copy your Hub address')}
        </Title>
        <RootButton secondary onClick={copyHubAddress} expand>
          {t('buttons.copyAddress', 'Copy HUB Address')}
        </RootButton>
      </Stack>
      <Text style={{ fontSize: 14 }}>
        {
          t(
            'components.airdrop.copyAddresDescription',
            'You will use this address to link your LAMINA1 Hub account to your Discord. If your browser does not have permission to use the clipboard, select and copy your address: {{address}}.',
            { address }
          ) as string
        }
      </Text>
      <Stack style={{ flexDirection: isMobile ? 'column' : 'row', gap: 30 }}>
        <Title order={4} style={{ maxWidth: 204 }}>
          {t('components.airdrop.openDiscord', '2. Open Discord, then follow the instructions')}
        </Title>
        <RootButton secondary onClick={openDiscord} expand>
          {t('buttons.openDiscord', 'Open Discord')}
        </RootButton>
      </Stack>
      <Text style={{ fontSize: 14 }}>
        <Trans i18nKey="components.airdrop.openDiscordDescription">
          Clicking this button will take you to the{' '}
          <a href={DISCORD_AIRDROP} target="_blank" rel="noreferrer">
            #hubmania-airdrop channel
          </a>{' '}
          on Discord, which will explain how to use our airdrop bot to link your Hub address and
          Discord account.
        </Trans>
        <> </>
        <Trans i18nKey="components.airdrop.airdropGuideMore">
          See the{' '}
          <a href={AIRDROP_GUIDE} target="_blank" rel="noreferrer">
            Hubmania Airdrop guide
          </a>{' '}
          for more details.
        </Trans>
      </Text>
      <Text style={{ fontSize: 14 }}>
        {t(
          'components.airdrop.skipText',
          'If you’ve already claimed your community rewards on a different account, feel free to skip this step.'
        )}
      </Text>
      <Stack style={{ flexDirection: 'row' }}>
        <RootButton secondary expand onClick={skip}>
          {t('buttons.continue', 'Continue')}
        </RootButton>
      </Stack>
    </Stack>
  )
}

export const getDiscordRewards = async (
  address: string,
  setRewards: Dispatch<SetStateAction<AirdropRecipient[] | undefined>>
): Promise<AirdropRecipient[]> => {
  // 1. RPC call to get linked Discord rewards - contract read
  const retval = (await readContract(getWagmiConfig(), {
    address: AIRDROP_CONTRACT_ADDRESS as `0x${string}`,
    account: address as `0x${string}`,
    abi: ClaimableAirdrop,
    functionName: 'getLinkedRewards',
    args: [address as `0x${string}`],
  })) as unknown as [number[], string[]]

  const linkedRewards: LinkedRewards | undefined =
    retval.length > 1
      ? {
          airdropID: retval[0],
          nodeID: retval[1],
        }
      : undefined

  if (
    linkedRewards &&
    linkedRewards.airdropID &&
    linkedRewards.nodeID &&
    linkedRewards.airdropID.length !== linkedRewards.nodeID.length
  ) {
    throw new Error('Error: Airdrop Discord linked rewards mismatch')
  }

  // 2. POST to get Discord Rewards from airdrop API - /airdrop/get/rewards
  const rewardsEndpoint = `${AIRDROP_URL}airdrop/get/rewards`
  const payload: RewardKey[] | undefined = linkedRewards?.airdropID
    ? linkedRewards.airdropID.map((aId, index) => ({
        AirdropID: Number(aId),
        NodeID: linkedRewards.nodeID[index],
      }))
    : undefined

  if (!payload || payload.length === 0) {
    console.log('No linked rewards')
    return []
  }

  const fetchRewards = async (): Promise<AirdropRecipient[]> => {
    try {
      const response = await fetch(rewardsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Method': 'POST',
          'x-api-key': AIRDROP_API_KEY,
        },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        if (response.body) {
          const data = await response.json()
          if (data) {
            throw new Error(`Error: ${data.error}`)
          }
        }
        throw new Error(response.statusText)
      }
      const data = (await response.json()) as AirdropRecipient[]
      return data
    } catch (error: any) {
      throw new Error(error.message)
    }
  }
  const dsRewards = payload && payload.length ? await fetchRewards() : []

  // Check which rewards are still valid from the address rewards - contract read
  const validRewards = (await readContract(getWagmiConfig(), {
    address: AIRDROP_CONTRACT_ADDRESS as `0x${string}`,
    account: address as `0x${string}`,
    abi: ClaimableAirdrop,
    functionName: 'getRewardStatuses',
    args: [
      dsRewards.map(({ AirdropID }) => BigInt(AirdropID)),
      dsRewards.map(({ NodeID }) => NodeID) as `0x${string}`[],
    ],
  })) as unknown as Reward[]

  const dsRewardsFiltered = dsRewards.filter(
    (_, idx) =>
      validRewards[idx].claimed === false &&
      validRewards[idx].revoked === false &&
      validRewards[idx].linkedAddress === address
  )

  // Filter out platform rewards that came from discord and link them to the address rewards
  const platformRewards = dsRewardsFiltered.filter(({ IsDiscord }) => !IsDiscord)

  setRewards((prevState: AirdropRecipient[] | undefined) => {
    if (prevState) {
      return prevState
        .map((prevReward: AirdropRecipient) => {
          const linkedReward = platformRewards.find(
            ({ AirdropID, NodeID }) =>
              AirdropID === prevReward.AirdropID && NodeID === prevReward.NodeID
          )
          if (linkedReward) {
            return {
              ...prevReward,
              RewardAmount: linkedReward.RewardAmount,
              MerkleProof: linkedReward.MerkleProof,
              IsPublic: linkedReward.IsPublic,
              IsDiscord: linkedReward.IsDiscord,
            }
          }
          return prevReward
        })
        .concat(
          platformRewards.filter(
            platformReward =>
              !prevState.some(
                (prevReward: AirdropRecipient) =>
                  prevReward.AirdropID === platformReward.AirdropID &&
                  prevReward.NodeID === platformReward.NodeID
              )
          )
        )
    }
    return platformRewards
  })

  return dsRewardsFiltered.filter(({ IsDiscord }) => IsDiscord)
}

interface LinkDiscordAirdropProps {
  prevStep: () => void
  nextStep: () => void
}

const LinkDiscordAirdrop: React.FC<LinkDiscordAirdropProps> = ({ prevStep, nextStep }) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address } = useAuth()

  const [, setRewards] = useSessionStorage<AirdropRecipient[]>(`addressRewards-${address}`)
  const [, setDiscordRewards] = useSessionStorage<AirdropRecipient[]>(
    `discordRewards-${address}`,
    []
  )
  const [linkedDiscord, setLinkedDiscord] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [airdropError, setAirdropError] = useState<string>('')

  const handleSyncAirdropBot = useCallback(async () => {
    setLinkedDiscord(true)
    if (!address) return
    try {
      setIsLoading(true)
      const retval = await getDiscordRewards(address, setRewards)
      setDiscordRewards(retval)
      setIsLoading(false)
      nextStep()
    } catch (error: any) {
      console.error(error)
      setIsLoading(false)
      setAirdropError(error.message)
    }
  }, [])

  const handleBackFromSync = useCallback(() => {
    setIsLoading(false)
    setAirdropError('')
  }, [])

  return (
    <Stack style={{ gap: '40px' }}>
      <Divider />
      {!linkedDiscord && <LinkDiscord skip={handleSyncAirdropBot} />}
      {linkedDiscord && !isLoading && airdropError && (
        <ErrorSyncingAirdrop errorMesssage={airdropError} retry={handleSyncAirdropBot} />
      )}
      {linkedDiscord && isLoading && !airdropError && (
        <SyncingAirdrop discord back={handleBackFromSync} />
      )}
      {linkedDiscord && !isLoading && !airdropError && (
        <Stack style={{ gap: '20px' }}>
          <Title order={isMobile ? 3 : 2}>
            {t('components.airdrop.interact', 'Get confirmation from the airdrop bot')}
          </Title>
          <Text style={{ fontSize: 14 }}>
            {t(
              'components.airdrop.interactDescription',
              'Once you’ve received this confirmation from the bot, click “Continue” below.'
            )}
          </Text>
          <img alt="discord-screenshot" src={airdropScreenshot} style={{ borderRadius: '12px' }} />
          <Stack style={{ flexDirection: 'row' }}>
            <RootButton secondary onClick={prevStep}>
              {t('buttons.back', 'Back')}
            </RootButton>
            <RootButton onClick={handleSyncAirdropBot} expand>
              {t('buttons.continue', 'Continue')}
            </RootButton>
          </Stack>
          <NeedHelp bot assistance />
        </Stack>
      )}
    </Stack>
  )
}
export default LinkDiscordAirdrop
